/** @jsx jsx */
import { jsx } from '@emotion/react';

import * as React from 'react';
import type { Link } from 'react-router';

import { snakeCase } from 'lodash';

import { useTheme } from '@coursera/cds-core';

import { TrackedReactLink } from 'bundles/page/components/TrackedLink2';

import _t from 'i18n!nls/admin-dashboard';

type Props = {
  text: string;
  tag: React.ElementType;
  htmlAttributes: { href: string; 'data-e2e': string };
  onClick?: AnchorOnClick | LinkOnClick | ButtonOnClick;
  buttonType?: 'button' | 'submit' | 'reset';
  isActive?: boolean;
  thirdPartyOrganizationId?: string;
  isBetaFeature?: boolean;
  isNewFeature?: boolean;
};

type AnchorOnClick = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => void;
type LinkOnClick = (event: React.MouseEvent<Link, MouseEvent>) => void;
type ButtonOnClick = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;

const MenuItem: React.FC<Props> = ({
  text,
  isActive,
  isBetaFeature,
  isNewFeature,
  htmlAttributes,
  tag = 'button',
  buttonType = 'button',
  thirdPartyOrganizationId,
  onClick,
}) => {
  const theme = useTheme();
  const children = (
    <span
      css={[
        {
          fontSize: '0.875rem',
          color: theme.palette.gray[600],
          ':hover': {
            color: theme.palette.black[700],
          },
        },
        isActive && {
          color: theme.palette.blue[600],
          fontWeight: 'bold',
          ':hover': {
            color: theme.palette.blue[600],
          },
        },
      ]}
    >
      {text}
      {isBetaFeature && (
        <span
          css={{
            backgroundColor: theme.palette.black[700],
            fontWeight: 'bold',
            color: 'var(--cds-color-white-0)',
            borderRadius: 10,
            padding: '2px 8px',
            marginLeft: 8,
          }}
        >
          {_t('BETA')}
        </span>
      )}
      {isNewFeature && (
        <span
          css={{
            backgroundColor: theme.palette.aqua[600],
            fontWeight: 'bold',
            color: theme.palette.black[700],
            borderRadius: 4,
            padding: '2px 8px',
            marginLeft: 8,
          }}
        >
          {_t('NEW')}
        </span>
      )}
    </span>
  );
  switch (tag) {
    case 'a': {
      const handleClick = onClick as AnchorOnClick;
      return (
        // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
        <a className="text-decoration-none" onClick={handleClick} {...htmlAttributes}>
          {children}
        </a>
      );
    }
    case 'link': {
      const { href, ...rest } = htmlAttributes;
      return (
        <TrackedReactLink
          trackingName={snakeCase(text)}
          data={{ thirdPartyOrganizationId }}
          style={{ textDecoration: 'none' }}
          href={href}
          {...rest}
        >
          {children}
        </TrackedReactLink>
      );
    }
    case 'button': {
      const handleClick = onClick as ButtonOnClick;

      return (
        <button
          className="button-link text-decoration-none"
          onClick={handleClick}
          {...htmlAttributes}
          // eslint-disable-next-line react/button-has-type
          type={buttonType}
        >
          {children}
        </button>
      );
    }
    default:
      return <span className="text-decoration-none inline-block">{children}</span>;
  }
};

export default MenuItem;
